import { Fragment, useEffect, useState } from "react";
import PageTitle from "../../layouts/PageTitle";
import config from "../../../../config.json";
import { deleteMenuForever, fetchAllMenus, restoreMenu } from "../../../services/MenuService";
import { Dropdown } from "react-bootstrap";
import imgVide from "../../../assets/images/vide.png";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";

import "../../../i18n";
import { useTranslation } from "react-i18next";

const MenuGridHistory = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [menus, setMenus] = useState([]);
  const [error, setError] = useState("");
  const [filteredMenus, setFilteredMenus] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedMenuIds, setSelectedMenuIds] = useState([]);

  const loadMenus = async () => {
    try {
      const data = await fetchAllMenus();
      if (data) {
        const archivedMenus = data.filter((menu) => menu.isArchived === true);
        setMenus(archivedMenus);
        setFilteredMenus(archivedMenus);
      } else {
        setError(t("noDataFound"));
      }
    } catch (err) {
      setError(t("errorLoadingMenu"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMenus();
  }, []);

  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      setFilteredMenus(menus);
      return;
    }

    const results = menus.filter((menu) => {
      const name =
        menu.menuDetails?.find((d) => d?.menuName)?.menuName ||
        menu.menuDetails?.[0]?.menuName ||
        menu.menuName ||
        "";
      return name.toString().toLowerCase().includes(term);
    });

    setFilteredMenus(results);
  }, [searchTerm, menus]);

  useEffect(() => {
    const validIds = menus.map((menu) => menu?._id).filter(Boolean);
    setSelectedMenuIds((prev) => prev.filter((id) => validIds.includes(id)));
  }, [menus]);

  const handleRestoreMenu = async (id) => {
    try {
      await restoreMenu(id);
      await loadMenus();
    } catch {
      // Error already shown by restoreMenu
    }
  };

  const handleDeleteForever = async (id) => {
    const willDelete = await swal({
      title: t("areYouSure") || "Are you sure?",
      text: t("deleteForeverWarning") || "This action cannot be undone.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (!willDelete) return;
    try {
      await deleteMenuForever(id);
      swal(t("successTitle") || "Success", t("deletedSuccessfully") || "Deleted permanently", "success");
      loadMenus();
    } catch (err) {
      swal(t("errorTitle") || "Error", err?.response?.data?.message || "Failed to delete permanently", "error");
    }
  };

  const visibleMenus = filteredMenus.filter((menu) => menu?.isArchived);
  const areAllSelected =
    visibleMenus.length > 0 &&
    visibleMenus.every((menu) => selectedMenuIds.includes(menu._id));
  const selectedCount = visibleMenus.filter((menu) =>
    selectedMenuIds.includes(menu._id)
  ).length;

  const toggleSelectOne = (id, checked) => {
    if (!id) return;
    setSelectedMenuIds((prev) => {
      if (checked) {
        if (prev.includes(id)) return prev;
        return [...prev, id];
      }
      return prev.filter((itemId) => itemId !== id);
    });
  };

  const toggleSelectAll = (checked) => {
    if (checked) {
      const ids = visibleMenus.map((menu) => menu._id).filter(Boolean);
      setSelectedMenuIds((prev) => Array.from(new Set([...prev, ...ids])));
      return;
    }
    const ids = new Set(visibleMenus.map((menu) => menu._id));
    setSelectedMenuIds((prev) => prev.filter((id) => !ids.has(id)));
  };

  const handleRestoreSelected = async () => {
    const selectedIds = visibleMenus
      .filter((menu) => selectedMenuIds.includes(menu._id))
      .map((menu) => menu._id);
    if (selectedIds.length === 0) return;

    const ok = await swal({
      title: t("areYouSure") || "Are you sure?",
      text: `Restore ${selectedIds.length} selected menu(s)?`,
      icon: "warning",
      buttons: true,
      dangerMode: false,
    });
    if (!ok) return;

    try {
      await Promise.all(selectedIds.map((id) => restoreMenu(id)));
      setSelectedMenuIds([]);
      await loadMenus();
    } catch (err) {
      swal(t("errorTitle") || "Error", "Failed to restore selected menus", "error");
    }
  };

  const handleDeleteSelected = async () => {
    const selectedIds = visibleMenus
      .filter((menu) => selectedMenuIds.includes(menu._id))
      .map((menu) => menu._id);
    if (selectedIds.length === 0) return;

    const ok = await swal({
      title: t("areYouSure") || "Are you sure?",
      text: `Delete ${selectedIds.length} selected menu(s) forever?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (!ok) return;

    try {
      await Promise.all(selectedIds.map((id) => deleteMenuForever(id)));
      setSelectedMenuIds([]);
      await loadMenus();
    } catch (err) {
      swal(t("errorTitle") || "Error", "Failed to delete selected menus", "error");
    }
  };
  return (
    <Fragment>
      <PageTitle activeMenu={t("archivedMenus")} motherMenu={t("history")} />
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="form-check m-0">
          <input
            id="select-all-menus-history"
            type="checkbox"
            className="form-check-input"
            checked={areAllSelected}
            onChange={(e) => toggleSelectAll(e.target.checked)}
            disabled={visibleMenus.length === 0}
          />
          <label className="form-check-label ms-2" htmlFor="select-all-menus-history">
            Select all ({visibleMenus.length})
          </label>
        </div>
        <div>
          <button
            className="btn btn-success btn-sm me-2"
            onClick={handleRestoreSelected}
            disabled={selectedCount === 0}
          >
            Restore Selected ({selectedCount})
          </button>
          <button
            className="btn btn-danger btn-sm"
            onClick={handleDeleteSelected}
            disabled={selectedCount === 0}
          >
            Delete Selected ({selectedCount})
          </button>
        </div>
      </div>
      <div className="row mt-5">
        {visibleMenus.length === 0 ? (
          <div className="col-12">
            <div className="alert alert-warning" role="alert">
              {t("noDataFound")} <i className="fa fa-spinner" />
            </div>
          </div>
        ) : (
          visibleMenus.map((item) => (
            <div className="col-xl-3 col-lg-6 col-md-6 col-sm-6" key={item._id}>
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-start mb-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={selectedMenuIds.includes(item._id)}
                      onChange={(e) => toggleSelectOne(item._id, e.target.checked)}
                    />
                  </div>
                  <div className="new-arrival-product">
                    <div className="new-arrivals-img-contnent">
                      <img
                        className="img-fluid"
                        src={
                          item?.photo?.length > 0
                            ? `${config.UPLOADS_SERVER}/menu/${item.photo}`
                            : imgVide
                        }
                        alt={item.menuDetails[0]?.menuName || "Menu Image"}
                        style={{
                          height: "250px",
                          objectFit: "cover",
                        }}
                      />

                      {/* Dropdown d'actions */}
                      <Dropdown className="position-absolute top-0 end-0 m-2 ">
                        <Dropdown.Toggle
                          variant="dark"
                          className="btn-sm border-0"
                        >
                          <i className="fa fa-ellipsis-v"></i>
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() =>
                              navigate(`/menu-details/${item._id}`)
                            }
                          >
                            <i className="fa fa-eye me-2"></i> {t("details")}
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => handleRestoreMenu(item._id)}
                          >
                            <i className="fa fa-archive me-2"></i>{" "}
                            {t("restore")}
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => handleDeleteForever(item._id)}>
                            <i className="fa fa-trash me-2 text-danger"></i>
                            {t("deleteForever") || "Delete forever"}
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>

                    {/* Nom du menu */}
                    <div className="new-arrival-content text-center mt-3">
                      <h4>{item.menuDetails[0]?.menuName || "No Name"}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </Fragment>
  );
};

export default MenuGridHistory;
